import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const hydroxychloroquine: ContentBlock<"medication"> = {
  id: makeId("med-hydroxychloroquine"),
  kind: "medication",
  tags: ["medications", "her", "during-cycle"],
  title: "Plaquenil",
  subtitle:
    "Hydroxychloroquine - immunomodulator used in specialist immune protocols",
  body: {
    genericName: "Hydroxychloroquine sulfate",
    drugClass: "Antimalarial / immunomodulator (DMARD)",
    purpose:
      "Hydroxychloroquine is an immune-modulating medicine that may be discussed for selected autoimmune or fertility-care situations. Fertility use can be off-label, and whether it belongs in an FET plan depends on review by the prescribing clinician and any relevant specialist. Evidence and treatment goals vary; this article cannot determine whether it is appropriate for you.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "There is no universal FET dose or schedule. Your prescribing clinician must confirm the product, dose, route, monitoring, start and stop dates, and whether it should continue during pregnancy. Do not change or stop treatment from this article.",
    howToTake:
      "Use hydroxychloroquine only as prescribed and follow your clinic's order or pharmacy label. Do not start, stop, change the dose, or change the timing from this article. Ask your prescribing clinician or pharmacist how to handle a late or missed dose and what monitoring is required, including eye or medication-safety checks. Report new vision changes, severe rash, muscle weakness, or a fast or irregular heartbeat to your care team.",
    sideEffects: [
      "Nausea, stomach upset, or diarrhea",
      "Headache or dizziness",
      "Skin rash or increased sun sensitivity",
      "Rare retinal (eye) changes with long-term use",
      "Mood changes or sleep disturbance",
      "Rare effects on heart rhythm or muscle strength",
    ],
    storage:
      "Store at room temperature, 20-25°C / 68-77°F, in the original container away from moisture and light. Keep well out of reach of children, as accidental ingestion can be very dangerous to them.",
    forWhom: "her",
    aliases: ["Hydroxychloroquine", "HCQ", "Plaquenil", "Quensyl"],
    availability: [
      {
        name: "Plaquenil",
        manufacturer: "Sanofi",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Quensyl",
        manufacturer: "Sanofi",
        countries: ["EU"],
      },
      {
        name: "Hydroxychloroquine (generic)",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
    ],
    frBody: {
      genericName: "Sulfate d'hydroxychloroquine",
      drugClass: "Antipaludéen / immunomodulateur (traitement de fond)",
      purpose:
        "L'hydroxychloroquine est un médicament immunomodulateur qui peut être discuté dans certaines situations auto-immunes ou de fertilité. Son usage en fertilité peut être hors AMM ; sa place dans un plan de TEC dépend de l'évaluation du prescripteur et, si nécessaire, d'un spécialiste. Les données et les objectifs varient ; cet article ne permet pas de déterminer si elle vous convient.",
      dosageRanges:
        "Il n'existe pas de dose ni de calendrier universels pour un TEC. Votre prescripteur doit confirmer le produit, la dose, la voie, la surveillance, les dates de début et d'arrêt, ainsi que la poursuite éventuelle pendant la grossesse. Ne modifiez ni n'arrêtez le traitement à partir de cet article.",
      howToTake:
        "Utilisez l'hydroxychloroquine uniquement sur prescription et suivez l'ordonnance de votre centre ou l'étiquette de la pharmacie. Ne commencez pas, n'arrêtez pas, ne modifiez pas la dose et ne changez pas le calendrier à partir de cet article. Demandez au prescripteur ou au pharmacien quoi faire en cas de dose retardée ou oubliée et quelle surveillance est nécessaire, notamment pour les yeux et la sécurité du traitement. Signalez à votre équipe toute modification de la vision, éruption sévère, faiblesse musculaire ou rythme cardiaque rapide ou irrégulier.",
      sideEffects: [
        "Nausées, troubles digestifs ou diarrhée",
        "Maux de tête ou vertiges",
        "Éruption cutanée ou sensibilité accrue au soleil",
        "Rares atteintes de la rétine en cas d'usage prolongé",
        "Changements d'humeur ou troubles du sommeil",
        "Rares effets sur le rythme cardiaque ou la force musculaire",
      ],
      storage:
        "À conserver à température ambiante, 20-25°C / 68-77°F, dans l'emballage d'origine, à l'abri de l'humidité et de la lumière. À tenir hors de portée des enfants, car une ingestion accidentelle peut être très dangereuse pour eux.",
    },
  },
};
registerBlock(hydroxychloroquine);

const filgrastim: ContentBlock<"medication"> = {
  id: makeId("med-filgrastim"),
  kind: "medication",
  tags: ["medications", "her", "during-cycle", "fet"],
  title: "Neupogen",
  subtitle:
    "Filgrastim (G-CSF) - growth factor used off-label for thin lining or implantation failure",
  body: {
    genericName: "Filgrastim (granulocyte colony-stimulating factor, G-CSF)",
    drugClass: "Granulocyte colony-stimulating factor (G-CSF)",
    purpose:
      "Filgrastim is a lab-made copy of granulocyte colony-stimulating factor (G-CSF), a natural protein that stimulates white blood cells and is thought to promote endometrial growth and a more receptive uterine lining. In fertility care it is used off-label, on a specialist's direction, given either as a subcutaneous injection or instilled directly into the uterus, mainly for thin endometrium or recurrent implantation failure around the time of embryo transfer. The evidence is mixed and still considered experimental, so it is not a standard IVF treatment. Use only within a clinic-directed protocol.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["pre_filled_syringe", "vial"],
    dosageRanges:
      "There is no universal FET dose or schedule. Your clinic must confirm the product, dose, route, administration setting, timing, and monitoring plan. Fertility protocols are not interchangeable with oncology treatment.",
    howToTake:
      "Use filgrastim only within the protocol prescribed by your clinic. The order must specify whether administration is subcutaneous or performed in clinic, along with the product, dose, timing, storage, and monitoring instructions. Do not prepare or administer an intrauterine treatment yourself. Ask your care team what to do about a late or missed dose and report significant bone or muscle pain, fever, severe upper-left abdominal or shoulder-tip pain, breathing difficulty, or signs of an allergic reaction.",
    sideEffects: [
      "Bone, joint, or muscle aches (most common)",
      "Headache",
      "Injection site redness, swelling, or soreness",
      "Mild fever or flu-like feeling",
      "Nausea",
      "Rare enlargement of the spleen or allergic reaction",
    ],
    storage:
      "Keep refrigerated at 2-8°C / 36-46°F in the original carton to protect from light. Do not freeze and do not shake. May be left at room temperature for a limited period before use as stated on the leaflet; discard if left out longer than allowed. Single-use only.",
    forWhom: "her",
    aliases: [
      "Filgrastim",
      "G-CSF",
      "Neupogen",
      "Zarzio",
      "Zarxio",
      "Nivestim",
      "Accofil",
    ],
    availability: [
      {
        name: "Neupogen",
        manufacturer: "Amgen",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Zarzio / Zarxio",
        manufacturer: "Sandoz",
        countries: ["US", "GB", "EU", "AU"],
      },
      {
        name: "Nivestim / Nivestym",
        manufacturer: "Pfizer",
        countries: ["US", "GB", "EU"],
      },
      {
        name: "Accofil",
        manufacturer: "Accord Healthcare",
        countries: ["GB", "EU"],
      },
    ],
    frBody: {
      genericName: "Filgrastim (facteur de croissance des granulocytes, G-CSF)",
      drugClass: "Facteur de croissance granulocytaire (G-CSF)",
      purpose:
        "Le filgrastim est une copie de laboratoire du facteur de croissance des granulocytes (G-CSF), une protéine naturelle qui stimule les globules blancs et que l'on pense capable de favoriser la croissance de l'endomètre et une muqueuse plus réceptive. En fertilité, il est utilisé hors AMM, sur prescription d'un spécialiste, en injection sous-cutanée ou en instillation directe dans l'utérus, surtout en cas d'endomètre fin ou d'échecs d'implantation à répétition au moment du transfert. Les données sont contrastées et encore expérimentales ; ce n'est pas un traitement standard de FIV. À utiliser uniquement dans un protocole dirigé par le centre.",
      dosageRanges:
        "Il n'existe pas de dose ni de calendrier universels pour un TEC. Votre centre doit confirmer le produit, la dose, la voie, le lieu d'administration, le calendrier et la surveillance nécessaires. Les protocoles de fertilité ne sont pas interchangeables avec les traitements en oncologie.",
      howToTake:
        "Utilisez le filgrastim uniquement dans le protocole prescrit par votre centre. L'ordonnance doit préciser si l'administration est sous-cutanée ou réalisée au centre, ainsi que le produit, la dose, le calendrier, la conservation et la surveillance. Ne préparez ni ne réalisez vous-même un traitement intra-utérin. Demandez à votre équipe quoi faire en cas de dose retardée ou oubliée et signalez des douleurs osseuses ou musculaires importantes, de la fièvre, une douleur sévère de la partie haute gauche de l'abdomen ou de la pointe de l'épaule, une difficulté à respirer ou des signes d'allergie.",
      sideEffects: [
        "Douleurs osseuses, articulaires ou musculaires (les plus fréquentes)",
        "Maux de tête",
        "Rougeur, gonflement ou douleur au point d'injection",
        "Légère fièvre ou syndrome pseudo-grippal",
        "Nausées",
        "Rare augmentation de la taille de la rate ou réaction allergique",
      ],
      storage:
        "À conserver au réfrigérateur, 2-8°C / 36-46°F, dans l'emballage d'origine pour protéger de la lumière. Ne pas congeler ni agiter. Peut rester à température ambiante un temps limité avant l'emploi, selon la notice ; à jeter au-delà. À usage unique.",
    },
  },
};
registerBlock(filgrastim);

const ivig: ContentBlock<"medication"> = {
  id: makeId("med-ivig"),
  kind: "medication",
  tags: ["medications", "her", "during-cycle", "fet"],
  title: "IVIG",
  subtitle:
    "Intravenous immunoglobulin - infusion used off-label in specialist immune protocols",
  body: {
    genericName:
      "Intravenous immunoglobulin (human normal immunoglobulin, IVIG)",
    drugClass: "Immunomodulator (pooled human immunoglobulin G)",
    purpose:
      "IVIG is a sterile preparation of antibodies pooled from thousands of human plasma donors, given as a slow infusion into a vein to modulate the immune system. In fertility care it is used off-label, on a reproductive immunologist's direction, in some immune-related recurrent miscarriage and recurrent implantation failure protocols, on the theory that it may dampen immune responses such as elevated natural killer cell activity. The evidence is limited and debated, so it is not a routine or proven treatment. It is also expensive and a blood-derived product, so it is reserved for carefully selected patients under specialist care.",
    commonRoutes: ["intravenous"],
    commonForms: ["vial"],
    dosageRanges:
      "There is no universal FET dose or infusion schedule. Your specialist must confirm the product, weight-based amount, number of infusions, timing, rate, and monitoring plan.",
    howToTake:
      "IVIG is given by trained staff in a hospital or infusion clinic; do not prepare or administer it yourself. Follow your specialist's order for the product, amount, timing, infusion rate, observation, and follow-up. Tell the team about prior reactions to immunoglobulins or blood products, IgA deficiency, kidney disease, clotting history, and current medicines. Ask what to do about a late or missed appointment and seek urgent care for severe or persistent headache with neck stiffness, trouble breathing, chest pain, a painful swollen leg, reduced urine output, or a serious allergic reaction.",
    sideEffects: [
      "Headache during or after infusion",
      "Chills, fever, or flu-like feeling",
      "Flushing, nausea, or fatigue",
      "Muscle or back aches",
      "Rare aseptic meningitis (severe headache with neck stiffness)",
      "Rare blood clots, kidney effects, or serious allergic reaction",
    ],
    storage:
      "Stored and handled at the clinic or pharmacy. Storage depends on the product: some (such as Privigen and Gammagard Liquid) are stable at room temperature up to 25°C / 77°F, while others (such as Octagam 10%) are kept refrigerated at 2-8°C / 36-46°F. Always keep in the original carton to protect from light, do not freeze, and do not shake.",
    forWhom: "her",
    aliases: [
      "IVIG",
      "IVIg",
      "Intravenous immunoglobulin",
      "Human normal immunoglobulin",
    ],
    availability: [
      {
        name: "Privigen",
        manufacturer: "CSL Behring",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Octagam",
        manufacturer: "Octapharma",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Gammagard Liquid",
        manufacturer: "Takeda",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Intratect",
        manufacturer: "Biotest",
        countries: ["GB", "EU"],
      },
    ],
    frBody: {
      genericName:
        "Immunoglobulines intraveineuses (immunoglobuline humaine normale)",
      drugClass: "Immunomodulateur (immunoglobuline G humaine poolée)",
      purpose:
        "Les IgIV sont une préparation stérile d'anticorps issus du plasma de milliers de donneurs, administrée en perfusion lente dans une veine pour moduler le système immunitaire. En fertilité, elles sont utilisées hors AMM, sur prescription d'un immunologiste de la reproduction, dans certains protocoles de fausses couches à répétition ou d'échecs d'implantation d'origine immunitaire, sur l'hypothèse qu'elles pourraient atténuer des réponses immunitaires comme une activité élevée des cellules NK. Les données sont limitées et débattues ; ce n'est pas un traitement de routine ni prouvé. C'est aussi un produit coûteux et dérivé du sang, réservé à des patientes soigneusement sélectionnées.",
      dosageRanges:
        "Il n'existe pas de dose ni de calendrier de perfusion universels pour un TEC. Votre spécialiste doit confirmer le produit, la quantité selon le poids, le nombre de perfusions, le calendrier, le débit et la surveillance nécessaires.",
      howToTake:
        "Les IgIV sont administrées par du personnel formé à l'hôpital ou dans un centre de perfusion ; ne les préparez pas et ne vous les administrez pas vous-même. Suivez l'ordonnance de votre spécialiste pour le produit, la quantité, le calendrier, le débit, la surveillance et le suivi. Signalez à l'équipe vos réactions antérieures aux immunoglobulines ou aux produits sanguins, un déficit en IgA, une maladie rénale, des antécédents de coagulation et vos médicaments actuels. Demandez quoi faire en cas de rendez-vous retardé ou manqué et consultez en urgence en cas de céphalée sévère ou persistante avec raideur de la nuque, difficultés respiratoires, douleur thoracique, jambe gonflée et douloureuse, baisse de la diurèse ou réaction allergique grave.",
      sideEffects: [
        "Mal de tête pendant ou après la perfusion",
        "Frissons, fièvre ou syndrome pseudo-grippal",
        "Bouffées de chaleur, nausées ou fatigue",
        "Douleurs musculaires ou dorsales",
        "Rare méningite aseptique (fort mal de tête avec raideur de la nuque)",
        "Rares caillots sanguins, effets rénaux ou réaction allergique grave",
      ],
      storage:
        "Stocké et manipulé au centre ou en pharmacie. La conservation dépend du produit : certains (Privigen, Gammagard Liquid) sont stables à température ambiante jusqu'à 25°C / 77°F, d'autres (Octagam 10%) sont conservés au réfrigérateur (2-8°C / 36-46°F). Toujours conserver dans l'emballage d'origine à l'abri de la lumière, ne pas congeler ni agiter.",
    },
  },
};
registerBlock(ivig);
